import { FC, MouseEvent } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown } from 'antd';
import {
    TbChevronDown,
    TbChevronRight,
    TbCopy,
    TbDotsVertical,
    TbExternalLink,
    TbEye,
    TbShieldLock,
    TbTopologyStar3,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CloudAttachment } from '@/services';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';

interface AttachmentItemActionsProps {
    attachment: CloudAttachment;
    folderOpen?: boolean;
    onFolderOpenChange?: (open: boolean) => void;
    onOpen?: (attachment: CloudAttachment) => void;
    onOpenChange?: (open: boolean) => void;
    onOntologyMode?: () => void;
    onRedactionMode?: () => void;
}

const AttachmentItemActions: FC<AttachmentItemActionsProps> = ({
    attachment,
    folderOpen,
    onFolderOpenChange,
    onOpen,
    onOpenChange,
    onOntologyMode,
    onRedactionMode,
}) => {
    const { token } = useAntDesignTheme();
    const isFolder = attachment.kind === 'folder';

    const copyLink = async () => {
        if (!attachment.webViewLink) {
            return;
        }

        try {
            await navigator.clipboard.writeText(attachment.webViewLink);
            toast.success(`${isFolder ? 'Folder' : 'Attachment'} link copied`);
        } catch (error) {
            captureException(error);
            toast.error(`Unable to copy ${isFolder ? 'folder' : 'attachment'} link`);
        }
    };

    const stopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation();
    const items = [
        isFolder && onFolderOpenChange
            ? {
                  icon: folderOpen ? <TbChevronDown /> : <TbChevronRight />,
                  key: 'toggle-folder',
                  label: folderOpen ? 'Collapse Folder' : 'Expand Folder',
                  onClick: () => onFolderOpenChange(!folderOpen),
              }
            : undefined,
        !isFolder && onOpen
            ? {
                  icon: <TbEye />,
                  key: 'view-attachment',
                  label: 'View Attachment',
                  onClick: () => onOpen(attachment),
              }
            : undefined,
        !isFolder && onOntologyMode
            ? {
                  icon: <TbTopologyStar3 />,
                  key: 'ontology-mode',
                  label: 'Ontology Mode',
                  onClick: onOntologyMode,
              }
            : undefined,
        !isFolder && onRedactionMode
            ? {
                  icon: <TbShieldLock />,
                  key: 'redaction-mode',
                  label: 'Secure Redaction',
                  onClick: onRedactionMode,
              }
            : undefined,
        attachment.webViewLink
            ? {
                  icon: <TbExternalLink />,
                  key: 'open-original',
                  label: 'Open Original',
                  onClick: () => window.open(attachment.webViewLink, '_blank', 'noopener,noreferrer'),
              }
            : undefined,
        attachment.webViewLink
            ? {
                  icon: <TbCopy />,
                  key: 'copy-link',
                  label: 'Copy Link',
                  onClick: () => void copyLink(),
              }
            : undefined,
    ].filter((item): item is NonNullable<typeof item> => Boolean(item));

    return (
        <Dropdown
            placement='bottomRight'
            trigger={['click']}
            onOpenChange={onOpenChange}
            popupRender={() => <DropdownMenuPanel items={items} style={{ minWidth: '13rem' }} />}
        >
            <Button
                aria-label={`Actions for ${attachment.name}`}
                icon={<TbDotsVertical style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />}
                size='small'
                type='text'
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
            />
        </Dropdown>
    );
};

export default AttachmentItemActions;
